/**
 * Active-filter breadcrumb chip row rendered on the left of the table
 * toolbar, next to the "Results" heading.
 *
 * Inputs come from `buildActiveFilterChips()` which collapses URL state
 * + lifted filter values into a list of removable chips:
 *   - Issue area path  ("Sale status" › "Voided sales")
 *   - Summary chip key ("Voided")
 *   - Each multiselect option, select non-default, toggle on, search term
 *
 * Path chips ("Sale status" and the sub-axis under it) get a thin `›`
 * separator between them so the path reads as a breadcrumb. The parent
 * stays non-removable while a sub-axis is in play — removing the sub
 * collapses back to the parent.
 *
 * Renders nothing when there are no chips — the toolbar's "Results"
 * heading carries the empty state on its own.
 */
import React from 'react'
import { Box, Chip, Stack } from '@mui/material'
import { ISSUE_CARD_ACTIVE_BG } from './IssueCard'
import { poolwerxColors } from '../../../theme/colors'

export type ActiveFilterChipKind = 'path-parent' | 'path-sub' | 'filter'

export interface ActiveFilterChip {
  /** Stable key for React + the `›` separator decision. */
  key: string
  label: string
  kind: ActiveFilterChipKind
  /** Omit to render a non-removable chip (eg. parent of an active sub). */
  onRemove?: () => void
}

interface ActiveFilterChipsProps {
  chips: ActiveFilterChip[]
}

const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({ chips }) => {
  if (chips.length === 0) return null
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        flexWrap: 'wrap',
        rowGap: 0.75,
        columnGap: 0.75,
        minWidth: 0,
      }}
    >
      {chips.map((chip, idx) => {
        const prev = chips[idx - 1]
        const showBreadcrumb =
          chip.kind === 'path-sub' && prev?.kind === 'path-parent'
        return (
          <React.Fragment key={chip.key}>
            {showBreadcrumb ? (
              <Box
                component="span"
                aria-hidden
                sx={{
                  color: 'text.disabled',
                  fontSize: '0.95rem',
                  lineHeight: 1,
                  mx: -0.25,
                }}
              >
                ›
              </Box>
            ) : null}
            <Chip
              size="small"
              label={chip.label}
              onDelete={chip.onRemove}
              sx={{
                height: 24,
                borderRadius: 1.5,
                fontWeight: 600,
                color: poolwerxColors.teal,
                backgroundColor: ISSUE_CARD_ACTIVE_BG,
                border: `1px solid transparent`,
                '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                '& .MuiChip-deleteIcon': {
                  color: poolwerxColors.teal,
                  fontSize: 16,
                  '&:hover': { color: poolwerxColors.tealHover },
                },
                '&:hover': { backgroundColor: ISSUE_CARD_ACTIVE_BG },
              }}
            />
          </React.Fragment>
        )
      })}
    </Stack>
  )
}

export default ActiveFilterChips
